// components/formDialog/DegreeDialog.tsx
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useAddDepartmentProgramMutation,
  useUpdateDepartmentProgramMutation,
  useProgramByIdQuery,
} from "@/hooks/api/department.queries";
import { masterKey } from "@/hooks/api/master.queries";
import { masterService } from "@/services/master.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as z from "zod";

type DegreeDialogProps = {
  mode: "create" | "edit";
  programId?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  deptId: string;
  onCancel: () => void;
};

const degreeSchema = z.object({
  degree_id: z.string().min(1, "Degree is required"),
  name: z.string().min(1, "Name is required"),
  code: z.string().min(1, "Code is required"),
});

type DegreeFormType = z.infer<typeof degreeSchema>;

const DegreeDialog = ({
  mode,
  programId,
  isOpen,
  onOpenChange,
  deptId,
  onCancel,
}: DegreeDialogProps) => {
  const addMutation = useAddDepartmentProgramMutation(deptId);
  const updateMutation = useUpdateDepartmentProgramMutation(deptId);
  const isPending = addMutation.isPending || updateMutation.isPending;

  const [degreeTypeFilter, setDegreeTypeFilter] = useState<"ALL" | "ug" | "pg">(
    "ALL"
  );

  const form = useForm<DegreeFormType>({
    resolver: zodResolver(degreeSchema),
    defaultValues: {
      degree_id: "",
      name: "",
      code: "",
    },
  });

  const { data: program, isLoading: isLoadingProgram } = useProgramByIdQuery(
    programId || "",
    isOpen && mode === "edit"
  );

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit") {
        if (program) {
          form.reset({
            degree_id: program.degree_id,
            name: program.name,
            code: program.code,
          });
        }
      } else {
        form.reset({
          degree_id: "",
          name: "",
          code: "",
        });
      }
    }
  }, [isOpen, mode, program, form]);

  const handleSubmit = (values: DegreeFormType) => {
    if (mode === "edit" && programId) {
      updateMutation.mutate(
        { programId, data: values },
        {
          onSuccess: () => {
            onCancel();
            form.reset();
          },
        }
      );
    } else {
      addMutation.mutate(values, {
        onSuccess: () => {
          onCancel();
          form.reset();
        },
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Update Degree" : "Add Degree to Department"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 py-4"
          >
            {isLoadingProgram && mode === "edit" ? (
              <div className="flex h-40 items-center justify-center">
                <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Degree Level
                  </label>
                  <Tabs
                    value={degreeTypeFilter}
                    onValueChange={(val) => {
                      setDegreeTypeFilter(val as any);
                      form.setValue("degree_id", "");
                    }}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="ALL">All Levels</TabsTrigger>
                      <TabsTrigger value="ug">UG</TabsTrigger>
                      <TabsTrigger value="pg">PG</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                <AsyncSingleSelectField<DegreeFormType>
                  key={degreeTypeFilter}
                  name="degree_id"
                  label="Degree"
                  placeholder="Select Degree"
                  fetchOptions={(params) =>
                    masterService.fetchCollegeDegreeOptions(
                      deptId,
                      params,
                      degreeTypeFilter !== "ALL" ? degreeTypeFilter : undefined
                    )
                  }
                  queryKey={[
                    ...masterKey.collegeDegrees(deptId),
                    degreeTypeFilter,
                  ]}
                  disabled={!deptId}
                  required
                />

                <InputField<DegreeFormType>
                  name="name"
                  label="Degree Name"
                  type="text"
                  placeholder="e.g. B.Tech Computer Science"
                  required
                />

                <InputField<DegreeFormType>
                  name="code"
                  label="Degree Code"
                  type="text"
                  placeholder="e.g. BTECH-CS"
                  required
                />
              </>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || isLoadingProgram}>
                {isPending
                  ? mode === "edit"
                    ? "Updating..."
                    : "Adding..."
                  : mode === "edit"
                    ? "Update Degree"
                    : "Add Degree"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DegreeDialog;
